import React, { useEffect, useState, useContext, useMemo } from "react";
import { useTheme, alpha } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { jwtDecode } from "jwt-decode";
import {
  getAllMateriales,
  materialFotoPublicUrl,
} from "../../api/Materialoteca/MaterialotecaApi";
import { useNavigate } from "react-router-dom";
import { SidebarContext } from "../../components/Sidebar/SidebarProvider";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Button,
  IconButton,
  TextField,
  Stack,
  Chip,
  InputAdornment,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import LibraryBooksOutlinedIcon from "@mui/icons-material/LibraryBooksOutlined";
import MaterialotecaDrawer from "./MaterialotecaDrawer";
import MaterialotecaPhotoLightbox from "./MaterialotecaPhotoLightbox";
import { numberLocaleFromI18n } from "../../utils/i18nLocale";
import { useProveedorPermissions } from "../../hooks/useProveedorPermissions"; // CHINA
import { formatFeriaLabel, muestraEstadoColor } from "../Ferias/feriaStatus";

/** Acento Materialoteca (tabla / CTAs), alineado con la pantalla previa */
const ACCENT = "#ffac7e";
const ACCENT_HOVER_BG = "#90b2b7";

const Materialoteca = () => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const dateLocale = useMemo(
    () => numberLocaleFromI18n(i18n.language),
    [i18n.language],
  );
  const { contentMargin } = useContext(SidebarContext);
  const [materiales, setMateriales] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filtro global
  const [search, setSearch] = useState("");

  // Drawer
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [photoLightbox, setPhotoLightbox] = useState(null);

  const openPhotoLightbox = (src, title) => {
    if (!src) return;
    setPhotoLightbox({ src, title: title || "" });
  };

  const closePhotoLightbox = () => setPhotoLightbox(null);

  const navigate = useNavigate();

  // 🇨🇳 CHINA — permisos por proveedor
  const { loading: proveedorLoading, filterByProveedor } =
    useProveedorPermissions();

  // 🔹 Control de login
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
    } else {
      try {
        const decodedToken = jwtDecode(token);
        const currentTime = Date.now() / 1000;
        if (decodedToken.exp < currentTime) {
          localStorage.removeItem("token");
          navigate("/login");
        }
      } catch (error) {
        console.error("Error decoding token:", error);
        navigate("/login");
      }
    }
  }, [navigate]);

  // 🔹 Cargar datos iniciales
  const loadData = async () => {
    try {
      const dataMat = await getAllMateriales();
      setMateriales(dataMat);
    } catch (err) {
      console.error("❌ Error cargando materiales:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleAdd = () => {
    setSelectedMaterial(null); // nuevo material
    setDrawerOpen(true);
  };

  const handleEdit = (material) => {
    setSelectedMaterial(material);
    setDrawerOpen(true);
  };

  const materialesAdaptados = materiales.map((mat) => ({
    ...mat,
    ProveedorNombre: mat.NombreProveedor, // 🔴 CLAVE
  }));

  // 🔍 Filtrado global (incluye fecha)
  const filteredMateriales = filterByProveedor(materialesAdaptados).filter(
    (mat) => {
      const query = search.toLowerCase();

      const fecha = mat.FechaCreacion
        ? new Date(mat.FechaCreacion).toLocaleDateString(dateLocale)
        : "";

      return (
        !query ||
        mat.NombreMaterial?.toLowerCase().includes(query) ||
        mat.NombreGrupo?.toLowerCase().includes(query) ||
        mat.NombreSubgrupo?.toLowerCase().includes(query) ||
        mat.Composicion?.toLowerCase().includes(query) ||
        mat.NombreProveedor?.toLowerCase().includes(query) ||
        fecha.toLowerCase().includes(query) ||
        (mat.Ferias || []).some((f) =>
          `${f.NombreFeria || ""} ${f.Anio || ""}`
            .toLowerCase()
            .includes(query),
        ) ||
        (mat.EstadoMuestra || "").toLowerCase().includes(query)
      );
    },
  );

  if (loading || proveedorLoading) {
    return (
      <Box
        sx={{
          ...contentMargin,
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 3,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            px: 5,
            py: 4,
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
            textAlign: "center",
          }}
        >
          <CircularProgress
            size={40}
            thickness={4}
            sx={{ color: ACCENT, mb: 2 }}
          />
          <Typography color="text.secondary" variant="body2">
            {t("MaterialotecaPage.loading")}
          </Typography>
        </Paper>
      </Box>
    );
  }

  const headCellSx = {
    fontWeight: 700,
    fontSize: "0.75rem",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    color: "text.secondary",
    borderBottom: `2px solid ${alpha(ACCENT, 0.35)}`,
    bgcolor: alpha(ACCENT, 0.08),
    whiteSpace: "nowrap",
  };

  const iconBtnSx = {
    border: `1px solid ${alpha(ACCENT, 0.55)}`,
    bgcolor: theme.palette.background.paper,
    color: ACCENT,
    borderRadius: 2,
    "&:hover": {
      bgcolor: ACCENT_HOVER_BG,
      borderColor: ACCENT_HOVER_BG,
      color: "#fff",
    },
  };

  return (
    <Box
      sx={{
        ...contentMargin,
        minHeight: "100%",
        bgcolor: alpha(theme.palette.primary.main, 0.02),
        px: { xs: 2, sm: 1 },
        py: { xs: 2, sm: 1 },
      }}
    >
      <Box sx={{ maxWidth: 1680, mx: "auto" }}>
        <Paper
          elevation={0}
          sx={{
            mb: 2.5,
            p: { xs: 2, sm: 2.5 },
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
            background: `linear-gradient(125deg, ${alpha(ACCENT, 0.2)} 0%, ${alpha(
              theme.palette.background.paper,
              1,
            )} 55%, ${alpha(theme.palette.divider, 0.06)} 100%)`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexDirection: { xs: "column", sm: "row" },
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            sx={{ flex: 1 }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  bgcolor: alpha(ACCENT, 0.28),
                  color: theme.palette.text.primary,
                }}
              >
                <LibraryBooksOutlinedIcon fontSize="medium" />
              </Box>
              <Box>
                <Typography
                  variant="overline"
                  sx={{
                    fontWeight: 800,
                    letterSpacing: 1,
                    color: "text.secondary",
                    lineHeight: 1.2,
                  }}
                >
                  {t("MaterialotecaPage.heroOverline")}
                </Typography>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 800, letterSpacing: -0.4 }}
                >
                  {t("MaterialotecaPage.heroTitle")}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  {t("MaterialotecaPage.heroSubtitle")}
                </Typography>
              </Box>
            </Stack>
          </Stack>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", sm: "center" }}
            sx={{ flex: 1, mb: { xs: 1, sm: 0 } }}
          >
            <TextField
              size="small"
              placeholder={t("MaterialotecaPage.searchPlaceholder")}
              variant="outlined"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              fullWidth
              sx={{
                maxWidth: { sm: 440 },
                "& .MuiOutlinedInput-root": { borderRadius: 2 },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      sx={{ color: "text.secondary", fontSize: 22 }}
                    />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAdd}
              sx={{
                alignSelf: { xs: "stretch", sm: "center" },
                py: 1.1,
                px: 2,
                fontWeight: 700,
                textTransform: "none",
                borderRadius: 2,
                bgcolor: ACCENT,
                color: theme.palette.getContrastText(ACCENT),
                boxShadow: "none",
                "&:hover": {
                  bgcolor: ACCENT_HOVER_BG,
                  boxShadow: `0 8px 20px ${alpha(ACCENT_HOVER_BG, 0.35)}`,
                },
              }}
            >
              {t("MaterialotecaPage.newMaterial")}
            </Button>
          </Stack>
          <Chip
            label={t("MaterialotecaPage.materialCount", {
              count: filteredMateriales.length,
            })}
            sx={{
              fontWeight: 700,
              bgcolor: alpha(ACCENT, 0.22),
              border: `1px solid ${alpha(ACCENT, 0.35)}`,
            }}
          />
        </Paper>

        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
            overflow: "auto",
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...headCellSx, width: 76 }}>
                  {t("MaterialotecaPage.colPhoto")}
                </TableCell>
                <TableCell sx={headCellSx}>
                  {t("MaterialotecaPage.colDate")}
                </TableCell>
                <TableCell sx={headCellSx}>
                  {t("MaterialotecaPage.colName")}
                </TableCell>
                <TableCell sx={headCellSx}>
                  {t("MaterialotecaPage.colGroup")}
                </TableCell>
                <TableCell sx={headCellSx}>
                  {t("MaterialotecaPage.colSubgroup")}
                </TableCell>
                <TableCell sx={headCellSx}>
                  {t("MaterialotecaPage.colComposition")}
                </TableCell>
                <TableCell sx={headCellSx}>
                  {t("MaterialotecaPage.colWeight")}
                </TableCell>
                <TableCell sx={headCellSx}>
                  {t("MaterialotecaPage.colWidth")}
                </TableCell>
                <TableCell sx={headCellSx}>
                  {t("MaterialotecaPage.colMoq")}
                </TableCell>
                <TableCell sx={headCellSx}>
                  {t("MaterialotecaPage.colPrice")}
                </TableCell>
                <TableCell sx={headCellSx}>
                  {t("MaterialotecaPage.colSupplier")}
                </TableCell>
                <TableCell sx={headCellSx}>
                  {t("MaterialotecaPage.colMill")}
                </TableCell>
                <TableCell sx={headCellSx}>
                  {t("MaterialotecaPage.colFair")}
                </TableCell>
                <TableCell sx={headCellSx}>
                  {t("MaterialotecaPage.colSampleStatus")}
                </TableCell>
                <TableCell align="right" sx={{ ...headCellSx, width: 72 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredMateriales.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={15} sx={{ py: 6, border: "none" }}>
                    <Typography
                      align="center"
                      color="text.secondary"
                      variant="body2"
                    >
                      {t("MaterialotecaPage.noResults")}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredMateriales.map((mat) => (
                  <TableRow
                    key={mat.Id}
                    hover
                    sx={{
                      "&:nth-of-type(even)": {
                        bgcolor: alpha(theme.palette.text.primary, 0.028),
                      },
                      "&:last-child td": { borderBottom: 0 },
                    }}
                  >
                    <TableCell sx={{ py: 1, width: 120 }}>
                      {(() => {
                        const archivos = [
                          ...(Array.isArray(mat.Fotos)
                            ? mat.Fotos.map((f) => f?.Archivo).filter(Boolean)
                            : []),
                          ...(mat.FotoArchivo ? [mat.FotoArchivo] : []),
                        ].filter(
                          (archivo, idx, arr) => arr.indexOf(archivo) === idx,
                        );

                        if (archivos.length === 0) {
                          return (
                            <Box
                              sx={{
                                width: 56,
                                height: 56,
                                borderRadius: 1,
                                bgcolor: alpha(
                                  theme.palette.text.primary,
                                  0.06,
                                ),
                              }}
                            />
                          );
                        }

                        const visible = archivos.slice(0, 3);
                        const extra = archivos.length - visible.length;

                        return (
                          <Stack
                            direction="row"
                            spacing={0.5}
                            alignItems="center"
                          >
                            {visible.map((archivo) => (
                              <Box
                                key={archivo}
                                component="button"
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  openPhotoLightbox(
                                    materialFotoPublicUrl(archivo),
                                    mat.NombreMaterial || "",
                                  );
                                }}
                                aria-label={t(
                                  "MaterialotecaPage.photoOpenLightboxAria",
                                  {
                                    subtitle: mat.NombreMaterial
                                      ? `: ${mat.NombreMaterial}`
                                      : "",
                                  },
                                )}
                                sx={{
                                  p: 0,
                                  m: 0,
                                  border: "none",
                                  background: "none",
                                  cursor: "pointer",
                                  borderRadius: 1,
                                  lineHeight: 0,
                                  display: "block",
                                  "&:focus-visible": {
                                    outline: `2px solid ${ACCENT}`,
                                    outlineOffset: 2,
                                  },
                                }}
                              >
                                <Box
                                  component="img"
                                  src={materialFotoPublicUrl(archivo)}
                                  alt=""
                                  sx={{
                                    width: 34,
                                    height: 34,
                                    objectFit: "cover",
                                    borderRadius: 1,
                                    border: `1px solid ${alpha(
                                      theme.palette.divider,
                                      0.2,
                                    )}`,
                                    display: "block",
                                  }}
                                />
                              </Box>
                            ))}
                            {extra > 0 && (
                              <Chip
                                size="small"
                                label={`+${extra}`}
                                sx={{ height: 22, fontSize: "0.7rem" }}
                              />
                            )}
                          </Stack>
                        );
                      })()}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {mat.FechaCreacion
                        ? new Date(mat.FechaCreacion).toLocaleDateString(
                            dateLocale,
                          )
                        : "—"}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {mat.NombreMaterial}
                    </TableCell>
                    <TableCell>{mat.NombreGrupo}</TableCell>
                    <TableCell>{mat.NombreSubgrupo}</TableCell>
                    <TableCell sx={{ maxWidth: 220 }}>
                      {mat.Composicion}
                    </TableCell>
                    <TableCell>{mat.Peso}</TableCell>
                    <TableCell>{mat.AnchoTirada}</TableCell>
                    <TableCell>{mat.MinimumOrderQty}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {mat.Precio != null ? `${mat.Precio} €` : "—"}
                    </TableCell>
                    <TableCell>{mat.NombreProveedor || "—"}</TableCell>
                    <TableCell>{mat.MillName || "—"}</TableCell>
                    <TableCell sx={{ maxWidth: 180 }}>
                      {(mat.Ferias || []).length === 0
                        ? "—"
                        : (mat.Ferias || []).map((f) => (
                            <Chip
                              key={`${f.id_FeriaEdicion}-${f.NombreFeria}`}
                              size="small"
                              label={formatFeriaLabel(f)}
                              sx={{
                                mr: 0.5,
                                mb: 0.35,
                                height: 22,
                                fontWeight: 600,
                                fontSize: "0.7rem",
                              }}
                            />
                          ))}
                    </TableCell>
                    <TableCell>
                      {mat.EstadoMuestra ? (
                        <Chip
                          size="small"
                          label={t(`FeriasPage.estado.${mat.EstadoMuestra}`)}
                          sx={{
                            fontWeight: 700,
                            bgcolor: alpha(
                              muestraEstadoColor(mat.EstadoMuestra),
                              0.14,
                            ),
                            color: muestraEstadoColor(mat.EstadoMuestra),
                          }}
                        />
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(mat)}
                        aria-label={t("MaterialotecaPage.editMaterialAria")}
                        sx={iconBtnSx}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <MaterialotecaPhotoLightbox
        open={Boolean(photoLightbox)}
        src={photoLightbox?.src}
        alt={photoLightbox?.title || ""}
        onClose={closePhotoLightbox}
        closeAriaLabel={t("MaterialotecaPage.photoLightboxCloseAria")}
      />

      {/* Drawer para añadir/editar */}
      <MaterialotecaDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSave={loadData}
        material={selectedMaterial}
      />
    </Box>
  );
};

export default Materialoteca;
